import { useState, useEffect } from "react";
import {
  START_DATE,
  FocusedInput,
  OnDatesChangeProps,
  END_DATE,
} from "@datepicker-react/hooks";
import AscDatePickerPopoverMonthView from "./AscDatePickerPopoverMonthView/AscDatePickerPopoverMonthView";
import { AscDatePickerPopoverProvider } from "./AscDatePickerPopoverContext/AscDatePickerPopoverContext";
import AscButton from "../../../AscButton/AscButton";
import styles from "./AscDatePickerPopover.module.scss";
import { ArrowLeftIcon, ArrowRightIcon } from "../../../AscIcons";
import { useCalendarDays } from "../../../../lib/helpers/hooks/useCalendarDays";

export interface AscDatePickerPopoverValue {
  startDate: Date | null;
  endDate: Date | null;
}
interface AscDatePickerPopoverProps {
  range?: boolean;
  value?: AscDatePickerPopoverValue;
  onChange?: (value: AscDatePickerPopoverValue) => void;
}
function AscDatePickerPopover({
  range,
  value,
  onChange,
}: AscDatePickerPopoverProps) {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [focusedInput, setFocusedInput] = useState<FocusedInput>(START_DATE);
  const {
    focusedDate,
    isDateSelected,
    isDateHovered,
    isFirstOrLastSelectedDate,
    isDateBlocked,
    isDateFocused,
    isStartDate,
    isEndDate,
    onDateHover,
    onDateSelect,
    onDateFocus,
    goToPreviousMonths,
    goToNextMonths,
    activeMonthLabel,
    calendarDaysForMonth,
    weekdayLabels,
    firstDayOfWeek,
  } = useCalendarDays({
    startDate: startDate,
    endDate: endDate,
    focusedInput: focusedInput,
    onDatesChange: handleDateChange,
    numberOfMonths: 1,
  });

  function handleDateChange(changedDate: OnDatesChangeProps) {
    setStartDate(changedDate.startDate);

    if (!range) {
      onChange?.({
        startDate: changedDate.startDate,
        endDate: null,
      });
    } else {
      setEndDate(changedDate.endDate);
      onChange?.({
        startDate: changedDate.startDate,
        endDate: changedDate.endDate,
      });
    }

    if (!changedDate.focusedInput) {
      setFocusedInput(START_DATE);
      return;
    }
    setFocusedInput(changedDate.focusedInput);
  }

  useEffect(() => {
    if (value && range) {
      setStartDate(value?.startDate);
      setEndDate(value?.endDate);
    } else {
      return;
    }
  }, [value, range]);

  useEffect(() => {
    if (!range || !startDate) {
      setFocusedInput(START_DATE);
      return;
    }
    if (!endDate) {
      setFocusedInput(END_DATE);
      return;
    }
    setFocusedInput(START_DATE);
  }, [startDate, endDate, range]);

  return (
    <AscDatePickerPopoverProvider
      {...{
        focusedDate,
        isDateFocused,
        isDateSelected,
        isDateHovered,
        isDateBlocked,
        isFirstOrLastSelectedDate,
        onDateSelect,
        onDateFocus,
        onDateHover,
        isStartDate,
        isEndDate,
        firstDayOfWeek,
      }}
    >
      <div className={styles.ground}>
        <div className={styles.heading}>
          <div className={styles.monthLabel}>{activeMonthLabel}</div>
          <div className={styles.navigationContainer}>
            <AscButton
              size="small"
              variant={"link"}
              onClick={goToPreviousMonths}
              hideLinkIcon
            >
              <ArrowLeftIcon className={styles.navigationIcon} />
            </AscButton>
            <AscButton
              size="small"
              variant={"link"}
              onClick={goToNextMonths}
              hideLinkIcon
            >
              <ArrowRightIcon className={styles.navigationIcon} />
            </AscButton>
          </div>
        </div>

        <div className={styles.calendarContainer}>
          <AscDatePickerPopoverMonthView
            calendarDaysForMonth={calendarDaysForMonth}
            weekdayLabels={weekdayLabels}
            range={range}
          />
        </div>
      </div>
    </AscDatePickerPopoverProvider>
  );
}

export default AscDatePickerPopover;
